import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Button, Skeleton } from '@heroui/react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTranslation } from '@/hooks/useTranslation';
import { useInfiniteUserUnfinishedGuides } from '@/hooks/useGuides';
import InProgressGuideCard from './InProgressGuideCard';

const UnfinishedGuidesSection: React.FC = () => {
  const { t } = useTranslation('home');
  const { currentLanguage } = useLanguage();
  const language = currentLanguage || 'en';

  const {
    data: unfinishedGuidesData,
    fetchNextPage: fetchNextUnfinishedPage,
    hasNextPage: hasNextUnfinishedPage,
    isFetchingNextPage: isFetchingNextUnfinishedPage,
    isLoading: unfinishedGuidesLoading,
  } = useInfiniteUserUnfinishedGuides(language);

  // Flatten unfinished guides
  const inProgressGuides = useMemo(() => {
    return (
      unfinishedGuidesData?.pages.flatMap((page) => {
        const response = page.data;
        return response || [];
      }) || []
    );
  }, [unfinishedGuidesData]);

  if (inProgressGuides.length === 0) {
    return null;
  }
  return (
    <motion.section
      className="mb-12"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.3 }}
    >
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{t('sections.inProgress.title')}</h2>
        <p className="text-lg text-gray-600 dark:text-gray-400">{t('sections.inProgress.subtitle')}</p>
      </div>

      {unfinishedGuidesLoading && inProgressGuides.length === 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-48 rounded-xl" />
          ))}
        </div>
      ) : inProgressGuides.length === 0 ? (
        <div className="text-center py-12 bg-white/60 dark:bg-gray-900/60 backdrop-blur-xl rounded-3xl border border-gray-200/50 dark:border-gray-800/50">
          <p className="text-gray-600 dark:text-gray-400">{t('noInProgress')}</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {inProgressGuides.map((item) => (
              <InProgressGuideCard
                key={item.progress.id}
                guide={item.guide}
                progress={item.progress}
                language={language}
              />
            ))}
          </div>

          {/* TODO: Add load more button */}
          {/* {hasNextUnfinishedPage && (
            <div className="flex justify-center">
              <Button
                color="primary"
                variant="flat"
                onPress={() => fetchNextUnfinishedPage()}
                isLoading={isFetchingNextUnfinishedPage}
                className="min-w-32"
              >
                {t('loadMore')}
              </Button>
            </div>
          )} */}
        </>
      )}
    </motion.section>
  );
};

export default UnfinishedGuidesSection;
